import { Button } from "@aitisal/ui/retail-storefront/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@aitisal/ui/retail-storefront/carousel";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ResponsiveImage from "../ResponsiveImage";
import { useBrandingConfig } from "../../utils/brandingConfig";

interface RetailBannerProps {
  isMobile?: boolean;
  autoRotateInterval?: number; // in milliseconds, default 5000 (5 seconds)
  showProgressBar?: boolean; // show progress bar for auto-rotation
}

const bannerAspectRatio = "80/33";

const RetailBanner: React.FC<RetailBannerProps> = ({
  isMobile,
  autoRotateInterval = 5000,
  showProgressBar = true,
}) => {
  const brandingConfig = useBrandingConfig();
  const images = brandingConfig.bannerImages || [];

  const [api, setApi] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef<NodeJS.Timeout | null>(null);

  const startAutoplay = useCallback(() => {
    if (!api || !isPlaying || images?.length <= 1) return;

    autoplayRef.current = setInterval(() => {
      if (!isHovered) {
        api.scrollNext();
      }
    }, autoRotateInterval);
  }, [api, isPlaying, autoRotateInterval, images?.length, isHovered]);

  const startProgress = useCallback(() => {
    if (!isPlaying || isHovered || images?.length <= 1) return;

    setProgress(0);
    progressRef.current = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + (100 / (autoRotateInterval / 100));
        return newProgress >= 100 ? 0 : newProgress;
      });
    }, 100);
  }, [isPlaying, isHovered, autoRotateInterval, images?.length]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
    if (progressRef.current) {
      clearInterval(progressRef.current);
      progressRef.current = null;
    }
  }, []);

  const toggleAutoplay = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      const selectedIndex = api.selectedScrollSnap();
      setCurrentSlide(selectedIndex);
      setProgress(0);
    };

    api.on("select", onSelect);
    onSelect();

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (isPlaying && !isHovered) {
      startAutoplay();
      startProgress();
    } else {
      stopAutoplay();
    }

    return stopAutoplay;
  }, [isPlaying, isHovered, startAutoplay, stopAutoplay, startProgress]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  if (!images || images?.length === 0) {
    return (
      <div
        className="flex w-full items-center justify-center bg-gray-100"
        style={{ aspectRatio: bannerAspectRatio }}
      >
        <div className="h-10 w-44 animate-pulse rounded-xl bg-gray-200/80" />
      </div>
    );
  }

  return (
    <div
      className="group relative w-full overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
        <Carousel
          setApi={setApi}
          opts={{
            loop: true,
            align: "center",
          }}
          className="w-full"
        >
          <CarouselContent className="ml-0 w-full">
            {images.map((image: any, index: number) => (
              <CarouselItem key={index} className="w-full pl-0">
                <div className="relative w-full overflow-hidden">
                  <ResponsiveImage
                    imageObject={image}
                    sizes="100vw"
                    alt={`Retail banner ${index + 1}`}
                    className="w-full h-full transition-transform duration-700 ease-out will-change-transform group-hover:scale-[1.03]"
                    style={{ aspectRatio: bannerAspectRatio }}
                    loading={index === 0 ? "eager" : "lazy"}
                    priority={index === 0}
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>

          {images?.length > 1 && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/30 via-black/10 to-transparent" />
          )}

          {/* Custom Navigation Buttons */}
          {images?.length > 1 && !isMobile && (
            <>
              {/* Previous Button */}
              <CarouselPrevious className="left-4 top-1/2 -translate-y-1/2 hidden sm:inline-flex opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 bg-black/60 hover:bg-black text-white border-none shadow-lg backdrop-blur-sm">
                <ChevronLeft className="h-5 w-5" />
              </CarouselPrevious>

              {/* Next Button */}
              <CarouselNext className="right-4 top-1/2 -translate-y-1/2 hidden sm:inline-flex opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 bg-black/60 hover:bg-black text-white border-none shadow-lg backdrop-blur-sm">
                <ChevronRight className="h-5 w-5" />
              </CarouselNext>
            </>
          )}

          {/* Progress bar for auto-rotation - flush at bottom edge */}
          {showProgressBar && images?.length > 1 && isPlaying && !isHovered && (
            <div className="absolute bottom-0 left-0 right-0 h-[3px] z-10 bg-white/20 overflow-hidden">
              <div
                className="h-full bg-white transition-all duration-100 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* Auto-play control button */}
          {images?.length > 1 && !isMobile && (
            <Button
              onClick={toggleAutoplay}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-300 backdrop-blur-sm opacity-0 group-hover:opacity-100"
              size="icon"
              variant="ghost"
              aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
              title={isPlaying ? "Pause slideshow" : "Play slideshow"}
            >
              {isPlaying ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>
          )}

          {/* Carousel indicators (dots) */}
          {images?.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
              {images.map((_: any, index: number) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => api?.scrollTo(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    currentSlide === index
                      ? "w-6 bg-white shadow-sm"
                      : "w-1.5 bg-white/50 hover:bg-white/80"
                  }`}
                />
              ))}
            </div>
          )}
        </Carousel>
    </div>
  );
};

export default RetailBanner;
